import { Editor } from "@tiptap/core";
import { Component, ComponentType, ReactNode, createRef } from "react";

import SearchModal from "@apps/search/search";
import { classNames } from "@helpers/css";
import { getTiptapSchema } from "@helpers/helpers";

import { icons } from "./commands-icons";
import EmbedUrlDialog from "./embeds/embed-url-dialog";
import GiphyDialog from "./embeds/giphy-dialog";

export const giphyCommandItem = {
  id: "giphy",
  title: "Image",
  keywords: "giphy gif photo image media",
  icon: icons.giphy,
  command: ({
    editor,
    range,
    props,
  }: {
    editor: Editor;
    range: any;
    props: any;
  }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .setImage({
        src: props.gif.images.original.webp,
        alt: props.gif.title,
        title: props.gif.title,
      })
      .run();
  },
};

export const embedUrlCommandItem = {
  id: "embedUrl",
  title: "Embed",
  keywords:
    "embed url link iframe youtube vimeo loom figma miro github jira hubspot salesforce clickup asana",
  icon: icons.embed,
};

export const findCommandItem = {
  id: "search",
  title: "Insert an artifact",
  keywords:
    "find search action item goal recognition hubspot jira github deal issue artifact",
  icon: icons.find,
  searchFilters: {},
  command: ({ editor }: { editor: Editor }) => {
    editor.chain().focus().run();
  },
};

export const explorerCommandItem = {
  id: "explorer",
  title: "Insert list of artifacts",
  keywords: "explorer list artifacts",
  icon: icons.explorer,
};

const defaultIndex = 1;

type Item = {
  id: string;
  title: string;
  keywords?: string;
  icon?: ComponentType;
  element?: ReactNode;
  subItems?: Item[];
  searchFilters?: any;
  command?: (args: { editor: Editor; range: any; props?: any }) => void;
};

type Props = {
  items: Item[];
  editor: any;
  command: any;
  range: any;
};

const dialogIds = new Set([
  giphyCommandItem.id,
  embedUrlCommandItem.id,
  findCommandItem.id,
]);

export class CommandList extends Component<Props> {
  state = {
    selectedIndex: defaultIndex,
    showDialog: null as string | null,
  };
  containerRef;

  constructor(props: Props) {
    super(props);
    this.containerRef = createRef<HTMLDivElement>();
  }

  componentDidUpdate(oldProps: any, oldState: any) {
    if (this.props.items !== oldProps.items) {
      this.setState({ selectedIndex: this.firstSelectableIndex() });
    }
    if (
      this.state.selectedIndex !== oldState.selectedIndex &&
      this.containerRef.current
    ) {
      const child = this.containerRef.current.children.item(
        this.state.selectedIndex,
      ) as HTMLDivElement;
      const parentHeight = this.containerRef.current.offsetHeight;
      const parentScrollTop = this.containerRef.current.scrollTop;
      if (!child) return;
      const childTop = child.offsetTop;
      const childHeight = child.offsetHeight;

      if (this.state.selectedIndex <= this.firstSelectableIndex()) {
        this.containerRef.current.scrollTo(0, 0);
      } else if (childTop + childHeight >= parentScrollTop + parentHeight) {
        const y = childTop + childHeight - parentHeight;
        this.containerRef.current.scrollTo(0, y);
      } else if (childTop < parentScrollTop) {
        this.containerRef.current.scrollTo(0, childTop);
      }
    }
  }

  firstSelectableIndex() {
    const idx = this.props.items.findIndex((item) => !item.subItems);
    return idx === -1 ? 0 : idx;
  }

  onKeyDown({ event }: any) {
    if (event.key === "ArrowUp") {
      this.upHandler();
      return true;
    }
    if (event.key === "ArrowDown") {
      this.downHandler();
      return true;
    }
    if (event.key === "Enter") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    if (event.key === "Tab") {
      this.enterHandler();
      return true;
    }
    return false;
  }

  upHandler() {
    let newIndex = this.state.selectedIndex - 1;
    while (newIndex > 0 && this.props.items[newIndex]?.subItems) {
      newIndex--;
    }
    if (newIndex >= 0 && !this.props.items[newIndex]?.subItems) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  downHandler() {
    let newIndex = this.state.selectedIndex + 1;
    while (
      newIndex < this.props.items.length &&
      this.props.items[newIndex]?.subItems
    ) {
      newIndex++;
    }
    if (newIndex < this.props.items.length) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex);
  }

  selectItem(index: number) {
    const item = this.props.items[index];
    if (item) {
      if (dialogIds.has(item.id)) {
        return this.setState({ showDialog: item.id });
      }
      // necessary though to let the tooltip/command dialog to close
      // allows to bring focus into artifact field
      setTimeout(() => {
        this.props.command(item);
      }, 150); // timeout number is made up
    }
  }

  handleClickSearchResult = (result: any) => {
    this.setState({ showDialog: null });
    const content = getTiptapSchema(result) as any;
    if (content) {
      this.props.editor
        .chain()
        .focus()
        .deleteRange(this.props.range)
        .insertContent(content)
        .run();
    } else {
      throw new Error(`Missing result: ${result.__typename}`);
    }
  };

  handleCloseDialog = () => {
    this.setState({ showDialog: null });
  };

  render() {
    const { items } = this.props;
    const { showDialog } = this.state;
    const matchingCommand = items.find(({ id }) => id === showDialog);
    return (
      <>
        {matchingCommand?.searchFilters && (
          <SearchModal
            initialFilters={{
              meetingGroupId:
                this.props.editor?.options?.editorProps?.meetingGroupId || null,
              ...matchingCommand.searchFilters,
            }}
            onClickResult={this.handleClickSearchResult}
            onClose={this.handleCloseDialog}
          />
        )}
        {showDialog === giphyCommandItem.id && (
          <GiphyDialog
            command={this.props.command}
            item={giphyCommandItem}
            onClose={this.handleCloseDialog}
          />
        )}
        {showDialog === embedUrlCommandItem.id && (
          <EmbedUrlDialog
            range={this.props.range}
            editor={this.props.editor}
            onClose={this.handleCloseDialog}
          />
        )}

        {!showDialog && items.length > 0 && (
          <div
            className="relative rounded bg-white border text-sm shadow js-wysiwyg-command overflow-y-auto scrollbar-minimal max-h-[28rem] w-72"
            aria-label="wysiwyg-commands"
            ref={this.containerRef}
          >
            {items.map((item, index: number) => {
              if (item.subItems) {
                return (
                  <div
                    key={String(item.id + item.title)}
                    className="pl-3 pr-2 pb-0.5 pt-3 mt-3 first:mt-0 text-2xs text-gray-500 uppercase tracking-tight font-medium border-t first:border-t-0"
                  >
                    {item.title}
                  </div>
                );
              }
              const Icon = item.icon;
              return (
                <button
                  className={classNames(
                    `flex items-center gap-2 w-full text-left pl-3 pr-2 py-1.5 text-gray-800 hover:bg-gray-50 hover:text-blue-600 font-medium tracking-tight`,
                    index === this.state.selectedIndex &&
                      "text-blue-600 bg-gray-100",
                  )}
                  key={String(item.id + item.title)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.selectItem(index);
                  }}
                >
                  {Icon && (
                    <span className="flex items-center justify-center w-4 h-4">
                      <Icon />
                    </span>
                  )}
                  <span className="truncate">{item.element || item.title}</span>
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  }
}
